import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Table, Spinner, Alert, Pagination, Button, Modal, Form, Badge, Row, Col, InputGroup } from 'react-bootstrap'; 
import ProgressDialog from './ProgressDialog';
import { get, post } from '../utils/api';
import config, { CURRENCY_SYMBOL } from '../config';
import { 
  FaCreditCard, FaMoneyBillWave, FaQrcode, FaUniversity, FaCoins,
  FaSearch, FaSyncAlt, FaFileInvoiceDollar, FaCheckCircle, FaClock,
  FaTimesCircle, FaUndo, FaPrint, FaThLarge, FaList, FaFilter,
  FaUser, FaCalendarAlt, FaTimes, FaSlidersH,
  FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronLeft, FaChevronRight,
  FaReceipt, FaExchangeAlt, FaShieldAlt, FaFileCsv, FaChevronDown
} from 'react-icons/fa';
import './PaymentManagement.css';
import NoDataCard from './NoDataCard';

const PaymentManagement = () => {
  const [paymentList, setPaymentList] = useState([]);
  const [serverMetrics, setServerMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(12); 

  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState(null);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [currentVerificationStatus, setCurrentVerificationStatus] = useState('');

  // Clean & Compact Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [timeframe, setTimeframe] = useState('all'); // 'all' | 'today' | '7days' | 'month' | 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'

  // Debounce search term by 350ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Handle Timeframe Selection
  const handleTimeframeChange = (val) => {
    setTimeframe(val);
    const now = new Date();
    const toYMD = (d) => d.toISOString().split('T')[0];

    if (val === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (val === 'today') {
      const todayStr = toYMD(now);
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (val === '7days') {
      const past = new Date();
      past.setDate(now.getDate() - 7);
      setStartDate(toYMD(past));
      setEndDate(toYMD(now));
    } else if (val === 'month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(toYMD(firstDay));
      setEndDate(toYMD(now));
    }
    setCurrentPage(1);
  };

  // Format date helper
  const formatPaymentDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return 'N/A';
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', limit);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (methodFilter && methodFilter !== 'all') params.append('method', methodFilter);
      if (debouncedSearch) params.append('q', debouncedSearch);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (sortBy) params.append('sort_by', sortBy);

      const response = await get(`/payments?${params.toString()}`);
      if (response && response.success && Array.isArray(response.data)) {
        setPaymentList(response.data);
        if (response.metrics) {
          setServerMetrics(response.metrics);
        }
        const meta = response.meta || {};
        setTotalPages(meta.total_pages || meta.totalPages || 1);
        setTotalItems(meta.total_items || meta.totalItems || response.data.length);
        setCurrentPage(meta.current_page || meta.currentPage || currentPage);
      } else if (response && Array.isArray(response.data)) {
        setPaymentList(response.data);
        setTotalItems(response.data.length);
      } else {
        setError(response?.message || 'Failed to fetch payments.');
        setPaymentList([]);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching payments.');
      setPaymentList([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, statusFilter, methodFilter, debouncedSearch, startDate, endDate, sortBy]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleShowStatusModal = (payment) => {
    setSelectedPayment(payment);
    let currentStatus = payment.payment_status || 'pending';
    if (currentStatus === 'success' || currentStatus === 'paid') currentStatus = 'completed';
    setCurrentVerificationStatus(currentStatus); 
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setSelectedPayment(null);
    setCurrentVerificationStatus('');
  };

  const handleShowDetailsModal = async (payment) => {
    setSelectedPayment(payment);
    setShowDetailsModal(true);
    try {
      const response = await get(`/payments/${payment.order_id}`);
      if (response && response.success) {
        setPaymentDetails(response.data);
      } else {
        setError('Failed to fetch payment details.');
      }
    } catch (error) {
      setError('Failed to fetch payment details.');
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedPayment(null);
    setPaymentDetails(null);
  };

  const handleModalVerifyPayment = async () => {
    if (selectedPayment && currentVerificationStatus) {
      await handleVerifyPayment(selectedPayment.order_id, selectedPayment.payment_id, currentVerificationStatus);
      handleCloseStatusModal(); 
    }
  };

  const handleVerifyPayment = async (order_id, payment_id, statusToSet) => { 
    setIsVerifying(true);
    setVerificationFeedback(null);
    try {
      const verifyPayload = {
        order_id: order_id,
        payment_id: payment_id,
        payment_order_id: `pay_order_${payment_id}`, 
        transaction_id: `txn_${payment_id}_${Date.now()}`, 
        status: statusToSet, 
        signature: 'dummy_signature_abc123', 
        remarks: `Status updated to ${statusToSet} by administrator`
      };

      const response = await post('/payments/verify', verifyPayload);

      if (response && response.success) {
        setVerificationFeedback({ type: 'success', message: response.message || `Payment marked as ${statusToSet} successfully!` });
        fetchPayments(); 
      } else {
        setVerificationFeedback({ type: 'danger', message: response?.message || `Payment verification to ${statusToSet} failed.` });
      }
    } catch (err) {
      setVerificationFeedback({ type: 'danger', message: err.message || 'An error occurred during verification.' });
    } finally {
      setIsVerifying(false);
    }
  };

  const getMethodIcon = (method) => {
    const m = String(method || '').toLowerCase();
    if (m.includes('cash')) return <FaMoneyBillWave className="text-success me-1" />;
    if (m.includes('card')) return <FaCreditCard className="text-primary me-1" />;
    if (m.includes('upi') || m.includes('online') || m.includes('qr')) return <FaQrcode className="text-warning me-1" />;
    if (m.includes('bank')) return <FaUniversity className="text-info me-1" />;
    return <FaCoins className="text-secondary me-1" />;
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'completed':
      case 'paid':
      case 'success':
        return <span className="clean-status-tag clean-paid"><FaCheckCircle className="me-1" /> Paid</span>;
      case 'pending':
        return <span className="clean-status-tag clean-pending"><FaClock className="me-1" /> Pending</span>;
      case 'failed':
        return <span className="clean-status-tag clean-failed"><FaTimesCircle className="me-1" /> Failed</span>;
      case 'refunded':
        return <span className="clean-status-tag clean-refunded"><FaUndo className="me-1" /> Refunded</span>;
      default:
        return <span className="clean-status-tag clean-pending">{status || 'Unknown'}</span>;
    }
  };

  // Financial Metrics Summary
  const metrics = useMemo(() => {
    if (serverMetrics) {
      return {
        totalTransactions: totalItems,
        totalVolume: serverMetrics.total_settled_volume || 0,
        completedCount: serverMetrics.count_completed || 0,
        pendingCount: serverMetrics.count_pending || 0,
        failedCount: serverMetrics.count_failed || 0
      };
    }
    let totalVolume = 0;
    let completedCount = 0;
    let pendingCount = 0;
    let failedCount = 0;

    paymentList.forEach(p => {
      const amt = parseFloat(p.amount || 0);
      const st = String(p.payment_status || '').toLowerCase();
      if (st === 'completed' || st === 'paid' || st === 'success') {
        totalVolume += amt;
        completedCount++;
      } else if (st === 'pending') {
        pendingCount++;
      } else {
        failedCount++;
      }
    });

    return {
      totalTransactions: totalItems || paymentList.length,
      totalVolume,
      completedCount,
      pendingCount,
      failedCount
    };
  }, [serverMetrics, paymentList, totalItems]);

  // Check if any filter is active
  const hasActiveFilters = Boolean(
    searchTerm.trim() || 
    statusFilter !== 'all' || 
    methodFilter !== 'all' || 
    timeframe !== 'all' || 
    startDate || 
    endDate
  );

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStatusFilter('all');
    setMethodFilter('all');
    setTimeframe('all');
    setStartDate('');
    setEndDate('');
    setSortBy('date_desc');
    setCurrentPage(1);
  };

  // Export to CSV Function
  const handleExportCSV = () => {
    if (!paymentList || paymentList.length === 0) return;
    const headers = ['Payment ID', 'Order ID', 'Customer Name', 'Mobile', 'Amount', 'Payment Method', 'Gateway', 'Status', 'Date'];
    const csvRows = [headers.join(',')];

    paymentList.forEach(p => {
      const row = [
        `"${p.payment_id || ''}"`,
        `"${p.order_id || ''}"`,
        `"${(p.customer_name || '').replace(/"/g, '""')}"`,
        `"${p.customer_mobile || ''}"`,
        `"${p.amount || 0}"`,
        `"${p.payment_method || ''}"`,
        `"${p.gateway_name || ''}"`,
        `"${p.payment_status || ''}"`,
        `"${p.payment_date || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `payments_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  return (
    <Container fluid className="pay-mgmt-page px-3 px-lg-4 py-2">
      <ProgressDialog show={loading && paymentList.length === 0} message="Loading financial transactions..." />

      {/* Clean Top Header Bar */}
      <div className="clean-top-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <div className="clean-header-icon">
            <FaCreditCard />
          </div>
          <div>
            <h4 className="clean-page-title mb-0">Payment Management</h4>
            <span className="clean-page-subtitle text-muted">
              {totalItems} total transactions • {CURRENCY_SYMBOL}{metrics.totalVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} collected
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button 
            type="button"
            onClick={handleExportCSV}
            disabled={paymentList.length === 0}
            className="clean-btn-ghost"
            title="Export filtered records to CSV"
          >
            <FaFileCsv className="me-1 text-primary" /> Export CSV
          </button>
          <button 
            type="button"
            onClick={fetchPayments} 
            className="clean-btn-ghost"
            title="Refresh payments ledger"
          >
            <FaSyncAlt className={`me-1 ${loading ? 'fa-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Verification Feedback Alert */}
      {verificationFeedback && (
        <Alert 
          variant={verificationFeedback.type} 
          onClose={() => setVerificationFeedback(null)} 
          dismissible
          className="mb-3 py-2 rounded-3 shadow-sm clean-alert"
        >
          {verificationFeedback.message}
        </Alert>
      )}

      {/* ==========================================================================
          CLEAN & COMPACT FILTER BAR (SaaS Minimalist Architecture)
          ========================================================================== */}
      <div className="clean-filter-bar mb-3 p-2 rounded-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          
          {/* Left Cluster: Search Input */}
          <div className="clean-search-container flex-grow-1">
            <div className="clean-search-box">
              <FaSearch className="clean-search-icon" />
              <input
                type="text"
                placeholder="Search Order #, Payment #, Customer, Phone, Amount..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="clean-search-field"
              />
              {searchTerm && (
                <button 
                  type="button" 
                  onClick={() => { setSearchTerm(''); setDebouncedSearch(''); }} 
                  className="clean-clear-icon-btn"
                  title="Clear search"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {/* Right Cluster: Compact Filter Pills */}
          <div className="clean-controls-cluster d-flex align-items-center gap-2 flex-wrap">
            
            {/* Status Dropdown Pill */}
            <div className="clean-select-wrapper">
              <select 
                value={statusFilter} 
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className={`clean-filter-select ${statusFilter !== 'all' ? 'active-filter' : ''}`}
              >
                <option value="all">Status: All ({metrics.totalTransactions})</option>
                <option value="completed">● Paid ({metrics.completedCount})</option>
                <option value="pending">● Pending ({metrics.pendingCount})</option>
                <option value="failed">● Failed ({metrics.failedCount})</option>
                <option value="refunded">● Refunded</option>
              </select>
            </div>

            {/* Method / Channel Dropdown Pill */}
            <div className="clean-select-wrapper">
              <select 
                value={methodFilter} 
                onChange={(e) => { setMethodFilter(e.target.value); setCurrentPage(1); }}
                className={`clean-filter-select ${methodFilter !== 'all' ? 'active-filter' : ''}`}
              >
                <option value="all">Channel: All</option>
                <option value="cash">💵 Cash</option>
                <option value="card">💳 Card</option>
                <option value="online">📱 UPI / QR</option>
                <option value="bank_transfer">🏦 Bank</option>
              </select>
            </div>

            {/* Timeframe Dropdown Pill */}
            <div className="clean-select-wrapper">
              <select 
                value={timeframe} 
                onChange={(e) => handleTimeframeChange(e.target.value)}
                className={`clean-filter-select ${timeframe !== 'all' ? 'active-filter' : ''}`}
              >
                <option value="all">Date: All Time</option>
                <option value="today">Today</option>
                <option value="7days">Last 7 Days</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range...</option>
              </select>
            </div>

            {/* Sort Dropdown Pill */}
            <div className="clean-select-wrapper">
              <select 
                value={sortBy} 
                onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                className="clean-filter-select"
              >
                <option value="date_desc">↕ Newest Date</option>
                <option value="date_asc">↕ Oldest Date</option>
                <option value="amount_desc">↕ Amount (High to Low)</option>
                <option value="amount_low">↕ Amount (Low to High)</option>
                <option value="customer_asc">↕ Customer Name</option>
              </select>
            </div>

            {/* Clean Segmented View Mode Toggle */}
            <div className="clean-view-toggle">
              <button 
                type="button" 
                className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table View"
              >
                <FaList />
              </button>
              <button 
                type="button" 
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <FaThLarge />
              </button>
            </div>

            {/* Reset All Filters Button (Only appears if filters are active) */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="clean-reset-btn"
                title="Reset all filters"
              >
                <FaTimes className="me-1" /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Expandable Custom Date Range (Only visible when 'custom' is active) */}
        {timeframe === 'custom' && (
          <div className="clean-custom-date-row mt-2 pt-2 border-top d-flex align-items-center gap-2 flex-wrap">
            <span className="clean-date-label">Range:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="clean-date-field"
            />
            <span className="text-muted small">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="clean-date-field"
            />
            {(startDate || endDate) && (
              <button 
                type="button" 
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="clean-link-btn"
              >
                Clear Dates
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {paymentList.length === 0 && !loading ? (
        <div className="clean-no-data-card py-5 text-center rounded-3">
          <NoDataCard 
            title="No Payment Records Found"
            message={hasActiveFilters ? "No transactions match your active filters." : "There are no payments recorded in the system."}
            icon="💳"
            actionButton={
              hasActiveFilters && (
                <Button variant="primary" size="sm" onClick={handleResetFilters} className="mt-2">
                  Clear Filters
                </Button>
              )
            }
          />
        </div>
      ) : viewMode === 'table' ? (
        /* Clean Minimalist Data Table */
        <div className="clean-table-card rounded-3 overflow-hidden">
          <Table responsive hover className="clean-data-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: '45px' }}>#</th>
                <th>Order Ref</th>
                <th>Payment ID</th>
                <th>Customer Details</th>
                <th>Payment Channel</th>
                <th>Amount</th>
                <th>Transaction Date</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paymentList.map((payment, index) => {
                const globalIndex = (currentPage - 1) * limit + index + 1;
                const formattedAmt = parseFloat(payment.amount || 0).toFixed(2);

                return (
                  <tr key={payment.payment_id || index}>
                    <td className="text-muted fw-bold small">#{globalIndex}</td>
                    <td>
                      <span className="clean-order-badge">
                        Order #{payment.order_id}
                      </span>
                    </td>
                    <td>
                      <span className="clean-id-code">#{payment.payment_id}</span>
                    </td>
                    <td>
                      <div className="clean-customer-name">
                        {payment.customer_name || 'Counter Customer'}
                      </div>
                      <div className="clean-customer-sub">
                        {payment.customer_mobile || ''}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center clean-channel-cell">
                        {getMethodIcon(payment.payment_method)}
                        <span className="text-capitalize">
                          {payment.payment_method || 'Offline Cash'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="clean-amount-text">
                        {CURRENCY_SYMBOL}{formattedAmt}
                      </span>
                    </td>
                    <td className="small text-muted">
                      {formatPaymentDate(payment.payment_date || payment.created_at)}
                    </td>
                    <td>
                      {getStatusBadge(payment.payment_status)}
                    </td>
                    <td className="text-end">
                      <div className="d-flex align-items-center justify-content-end gap-1">
                        <button 
                          type="button" 
                          className="clean-table-btn"
                          onClick={() => handleShowDetailsModal(payment)}
                          title="View Official Receipt"
                        >
                          <FaReceipt className="me-1" /> Receipt
                        </button>
                        <button 
                          type="button" 
                          className="clean-table-btn-icon"
                          onClick={() => handleShowStatusModal(payment)}
                          title="Update Status"
                        >
                          <FaExchangeAlt />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        /* Clean Cards Grid */
        <div className="clean-cards-grid">
          {paymentList.map((payment, index) => {
            const globalIndex = (currentPage - 1) * limit + index + 1;
            const formattedAmt = parseFloat(payment.amount || 0).toFixed(2);

            return (
              <div className="clean-card-item" key={payment.payment_id || index}>
                {/* Header */}
                <div className="clean-card-header p-3 border-bottom d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <div className="clean-card-channel-icon">
                      {getMethodIcon(payment.payment_method)}
                    </div>
                    <div>
                      <div className="clean-card-id">Payment #{payment.payment_id}</div>
                      <span className="clean-card-sub">Order #{payment.order_id}</span>
                    </div>
                  </div>
                  <span className="clean-card-num">#{globalIndex}</span>
                </div>

                {/* Body */}
                <div className="clean-card-body p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small">Customer:</span>
                    <span className="clean-card-val fw-semibold small">{payment.customer_name || 'Counter Customer'}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small">Amount:</span>
                    <span className="clean-card-amount">{CURRENCY_SYMBOL}{formattedAmt}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small">Channel:</span>
                    <span className="clean-card-val text-capitalize fw-semibold small">{payment.payment_method || 'Offline'}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small">Date:</span>
                    <span className="small text-muted">{formatPaymentDate(payment.payment_date || payment.created_at)}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <span className="text-muted small">Status:</span>
                    {getStatusBadge(payment.payment_status)}
                  </div>
                </div>

                {/* Footer */}
                <div className="clean-card-footer p-2 bg-transparent border-top d-flex gap-2">
                  <button 
                    type="button"
                    className="clean-card-action-btn flex-grow-1"
                    onClick={() => handleShowDetailsModal(payment)}
                  >
                    <FaReceipt className="me-1" /> Receipt
                  </button>
                  <button 
                    type="button" 
                    className="clean-card-icon-btn"
                    onClick={() => handleShowStatusModal(payment)}
                    title="Update Status"
                  >
                    <FaExchangeAlt />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Bar */}
      {totalPages > 1 && (
        <div className="clean-pagination-row d-flex justify-content-between align-items-center flex-wrap gap-2 p-2 px-3 rounded-3 mt-3">
          <div className="clean-pagination-info text-muted small">
            Page <span className="clean-page-current fw-bold">{currentPage}</span> of <span className="clean-page-current fw-bold">{totalPages}</span> ({totalItems} records)
          </div>

          <Pagination className="clean-pagination-nav mb-0">
            <Pagination.First 
              onClick={() => setCurrentPage(1)} 
              disabled={currentPage <= 1}
              title="First Page"
            >
              <FaAngleDoubleLeft />
            </Pagination.First>
            <Pagination.Prev 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
              disabled={currentPage <= 1}
              title="Previous Page"
            >
              <FaChevronLeft />
            </Pagination.Prev>

            {Array.from({ length: Math.min(totalPages, 7) }).map((_, idx) => {
              const pageNum = idx + 1;
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}

            <Pagination.Next 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
              disabled={currentPage >= totalPages}
              title="Next Page"
            >
              <FaChevronRight />
            </Pagination.Next>
            <Pagination.Last 
              onClick={() => setCurrentPage(totalPages)} 
              disabled={currentPage >= totalPages}
              title="Last Page"
            >
              <FaAngleDoubleRight />
            </Pagination.Last>
          </Pagination>
        </div>
      )}

      {/* Update Status Modal */}
      <Modal 
        show={showStatusModal} 
        onHide={handleCloseStatusModal} 
        scrollable
        className="pay-modal-modern"
      >
        <Modal.Header closeButton className="pay-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="pay-modal-icon">
              <FaShieldAlt />
            </div>
            <div>
              <Modal.Title className="pay-modal-title mb-0">Update Settlement Status</Modal.Title>
              <div className="pay-modal-subtitle">
                Order <span className="fw-bold text-primary">#{selectedPayment?.order_id}</span> • Payment <span className="fw-bold text-info">#{selectedPayment?.payment_id}</span>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="pay-modal-body p-4">
          {selectedPayment && (
            <div>
              <div className="pay-summary-box p-3 rounded mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="pay-summary-label">Transaction Amount:</span>
                  <span className="pay-amount-highlight">{CURRENCY_SYMBOL}{parseFloat(selectedPayment.amount || 0).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="pay-summary-label">Customer Name:</span>
                  <span className="pay-summary-value">{selectedPayment.customer_name || 'Counter Customer'}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="pay-summary-label">Current Status:</span>
                  <span className="pay-summary-status-tag">{selectedPayment.payment_status || 'pending'}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="pay-summary-label">Payment Channel:</span>
                  <span className="pay-summary-value text-capitalize">
                    {getMethodIcon(selectedPayment.payment_method)} {selectedPayment.payment_method || 'Offline Cash'}
                  </span>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="pay-modal-label">Select New Verification Status</Form.Label>
                <Form.Select
                  value={currentVerificationStatus}
                  onChange={(e) => setCurrentVerificationStatus(e.target.value)}
                  disabled={isVerifying}
                  className="pay-status-select"
                >
                  <option value="pending">⏳ Pending Review</option>
                  <option value="completed">✅ Completed / Paid</option>
                  <option value="failed">❌ Failed / Rejected</option>
                  <option value="refunded">🔄 Refunded</option>
                </Form.Select>
              </Form.Group>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="pay-modal-footer d-flex justify-content-between py-3 px-4">
          <Button variant="secondary" onClick={handleCloseStatusModal} disabled={isVerifying} className="pay-modal-cancel-btn">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleModalVerifyPayment} disabled={isVerifying} className="pay-modal-confirm-btn fw-bold px-4">
            {isVerifying ? <><Spinner animation="border" size="sm" className="me-1" /> Saving...</> : 'Confirm Settlement Status'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Official Receipt Modal */}
      <Modal 
        show={showDetailsModal} 
        onHide={handleCloseDetailsModal} 
        size="lg"
        scrollable
        className="pay-receipt-modal"
      >
        <Modal.Header closeButton className="pay-modal-header py-3 px-4 no-print">
          <div className="d-flex align-items-center gap-3">
            <div className="pay-modal-icon receipt">
              <FaReceipt />
            </div>
            <div>
              <Modal.Title className="pay-modal-title mb-0">Official Payment Receipt</Modal.Title>
              <div className="pay-modal-subtitle small">Payment #{paymentDetails?.payment_id || selectedPayment?.payment_id}</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-3 p-md-4">
          {paymentDetails ? (
            <div className="official-receipt-card p-3 p-md-4 rounded-3 shadow-sm">
              {/* Receipt Header */}
              <div className="receipt-brand-header text-center mb-3 pb-2">
                <div className="receipt-store-name fw-bold fs-4">{config.appName} Pharmacy</div>
                <div className="receipt-tagline small">Point of Sale & Online Settlement Slip</div>
                <div className="receipt-invoice-num small mt-2">
                  <span className="badge receipt-invoice-badge px-3 py-1">
                    Invoice #{paymentDetails.payment_id}
                  </span>
                </div>
              </div>

              <Row className="g-3 mb-3">
                <Col sm={6}>
                  <div className="receipt-info-block">
                    <div className="receipt-info-title small mb-1">Customer Information</div>
                    <div className="receipt-data-value fw-bold">{paymentDetails.customer_name || 'Counter Customer'}</div>
                    <div className="small receipt-data-muted">{paymentDetails.customer_mobile || 'Mobile: N/A'}</div>
                    <div className="small receipt-data-muted">{paymentDetails.customer_email || ''}</div>
                  </div>
                </Col>
                <Col sm={6} className="text-sm-end">
                  <div className="receipt-info-block">
                    <div className="receipt-info-title small mb-1">Transaction Metadata</div>
                    <div className="small receipt-data-muted">Date: <strong className="receipt-data-strong">{formatPaymentDate(paymentDetails.payment_date || paymentDetails.order_date)}</strong></div>
                    <div className="small receipt-data-muted">Order ID: <strong className="receipt-data-strong">#{paymentDetails.order_id}</strong></div>
                    <div className="small receipt-data-muted">Gateway: <strong className="receipt-data-strong">{paymentDetails.gateway_name || 'POS Counter'}</strong></div>
                  </div>
                </Col>
              </Row>

              {/* Payment Details Box */}
              <div className="receipt-amount-box p-3 rounded-3 mb-3 text-center">
                <div className="small receipt-amount-label text-uppercase fw-bold letter-spacing-1">Amount Paid</div>
                <div className="receipt-grand-amount fw-bold fs-2 my-1">
                  {CURRENCY_SYMBOL}{parseFloat(paymentDetails.amount || paymentDetails.total_amount || 0).toFixed(2)}
                </div>
                <div className="d-flex justify-content-center align-items-center gap-2 mt-2 flex-wrap">
                  <span className="small receipt-data-muted">Payment Mode:</span>
                  <span className="badge receipt-method-badge text-capitalize px-2 py-1">
                    {getMethodIcon(paymentDetails.payment_method)} {paymentDetails.payment_method || 'Offline Cash'}
                  </span>
                  <span className="small receipt-data-muted ms-2">Status:</span>
                  {getStatusBadge(paymentDetails.payment_status)}
                </div>
              </div>

              {/* Footer Note */}
              <div className="receipt-footer-note text-center small pt-3">
                Thank you for choosing {config.appName}. This is a computer generated receipt.
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="receipt-data-muted small mt-2">Fetching receipt details...</p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="pay-modal-footer d-flex justify-content-between py-2 px-4 no-print">
          <Button variant="outline-primary" onClick={handlePrintReceipt} className="pay-print-receipt-btn d-flex align-items-center gap-2">
            <FaPrint /> Print Receipt
          </Button>
          <Button variant="secondary" onClick={handleCloseDetailsModal} className="pay-modal-cancel-btn">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PaymentManagement;
